import React, { useState, useEffect } from 'react';
import { useChatContext } from '../contexts/ChatContext';

const CreateGroupModal = ({ onClose }) => {
  const { users, createGroup, loadUsers } = useChatContext();
  const [groupName, setGroupName] = useState('');
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Load users if not already loaded
    if (users.length === 0) {
      loadUsers();
    }
  }, [users.length, loadUsers]);

  const handleMemberToggle = (userId) => {
    setSelectedMembers(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!groupName.trim()) {
      setError('Please enter a group name');
      return;
    }

    if (selectedMembers.length === 0) {
      setError('Please select at least one member');
      return;
    }

    setCreating(true);
    setError(null);

    try {
      await createGroup(groupName.trim(), selectedMembers);
      onClose();
    } catch (err) {
      setError('Failed to create group. Please try again.');
    } finally {
      setCreating(false);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="create-group-overlay" onClick={handleOverlayClick}>
      <div className="create-group-modal">
        <div className="create-group-header">
          <h3>Create Group</h3>
          <button className="modal-close-btn" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="create-group-content">
            {error && (
              <div className="chat-error" style={{ marginBottom: '16px' }}>
                {error}
              </div>
            )}

            <div className="create-group-form-group">
              <label htmlFor="groupName">Group Name</label>
              <input
                id="groupName"
                type="text"
                className="create-group-input"
                placeholder="Enter group name..."
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                autoFocus
              />
            </div>

            <div className="create-group-form-group">
              <label>Select Members</label>
              <div className="member-select-list">
                {users.length === 0 ? (
                  <div style={{ padding: '20px', textAlign: 'center', color: '#888' }}>
                    No users available
                  </div>
                ) : (
                  users.map(user => (
                    <div
                      key={user.id}
                      className={`member-select-item ${selectedMembers.includes(user.id) ? 'selected' : ''}`}
                      onClick={() => handleMemberToggle(user.id)}
                    >
                      <input
                        type="checkbox"
                        className="member-checkbox"
                        checked={selectedMembers.includes(user.id)}
                        onChange={() => handleMemberToggle(user.id)}
                      />
                      <div className="member-select-info">
                        <div className="member-select-name">
                          {user.name || user.username}
                        </div>
                        <div className="member-select-email">
                          {user.email || `@${user.username}`}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          <div className="create-group-actions">
            <button type="button" className="btn-cancel" onClick={onClose}>
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={creating || !groupName.trim() || selectedMembers.length === 0}
            >
              {creating ? 'Creating...' : 'Create Group'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateGroupModal;
